import { Grid, GridRow, ModalBottomButtons, TextArea } from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { CombinedError } from 'urql';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  useCreateSubstitutionAnnouncementsMutation,
  useSubstitutionAnnouncementsQuery,
  useUpdateSubstitutionAnnouncementsMutation,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { useSubstitutionStore } from '../SubstitutionProvider';

type SubstitutionAnnouncementsFormType = {
  uuid: string;
  date: string;
  publicAnnouncement: string;
  internalAnnouncement: string;
};

type SubstitutionAnnouncementsFormProps = {
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const SubstitutionAnnouncementsForm = observer(({ onLoad, onClose }: SubstitutionAnnouncementsFormProps) => {
  const { t } = useTranslation();
  const { dateString } = useSubstitutionStore();
  const context = useMemorizedCacheTag('SUBSTITUTION_ANNOUNCEMENTS');

  const [, create] = useCreateSubstitutionAnnouncementsMutation();
  const [, update] = useUpdateSubstitutionAnnouncementsMutation();

  const [{ data: announcementsData }] = useSubstitutionAnnouncementsQuery({
    variables: { selectedDay: dateString },
    pause: !dateString,
    context,
  });

  const announcement: SubstitutionAnnouncementsFormType | null = announcementsData?.substitutionAnnouncements[0]
    ? {
        uuid: announcementsData?.substitutionAnnouncements[0].uuid,
        date: announcementsData?.substitutionAnnouncements[0].date,
        publicAnnouncement: announcementsData?.substitutionAnnouncements[0].publicAnnouncement ?? '',
        internalAnnouncement: announcementsData?.substitutionAnnouncements[0].internalAnnouncement ?? '',
      }
    : null;

  const initialValues: SubstitutionAnnouncementsFormType = {
    uuid: announcement?.uuid ?? '',
    date: announcement?.date ?? dateString,
    publicAnnouncement: announcement?.publicAnnouncement ?? '',
    internalAnnouncement: announcement?.internalAnnouncement ?? '',
  };

  async function handleSubmit(
    values: SubstitutionAnnouncementsFormType,
    formHelpers: FormikHelpers<SubstitutionAnnouncementsFormType>,
  ) {
    onLoad(true);

    let mutationResult: { error?: CombinedError | undefined };

    if (!announcement) {
      mutationResult = await create(
        {
          input: {
            date: dateString,
            internalAnnouncement: values.internalAnnouncement,
            publicAnnouncement: values.publicAnnouncement,
          },
        },
        context,
      );
    } else {
      mutationResult = await update(
        {
          update: {
            date: dateString,
            internalAnnouncement: values.internalAnnouncement,
            publicAnnouncement: values.publicAnnouncement,
          },
          where: { uuid: values.uuid },
        },
        context,
      );
    }

    if (mutationResult.error) {
      showUserErrorToast({ error: mutationResult.error });
    } else {
      if (announcement) {
        showSuccessUpdateToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  return (
    <Formik<SubstitutionAnnouncementsFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors, submitForm }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <TextArea
                  value={values.publicAnnouncement}
                  onChange={async (e) => {
                    await setFieldValue('publicAnnouncement', e.target.value);
                  }}
                  label={t('announcements.public')}
                  name='public'
                  rows={6}
                  isScrollable
                />
              </GridRow>
              <GridRow spacingTop='s'>
                <TextArea
                  value={values.internalAnnouncement}
                  onChange={async (e) => {
                    await setFieldValue('internalAnnouncement', e.target.value);
                  }}
                  label={t('announcements.internal')}
                  name='internal'
                  rows={6}
                  isScrollable
                />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                callback: async () => {
                  await submitForm();
                },
                disabled: isSubmitting || !dirty,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
});
